// The filename a download names in its Content-Disposition header (RFC 6266).
//
// The api's send_data names every file twice:
//
//   attachment; filename="W-9 %28signed%29%2C Smith %26 Sons.pdf"; filename*=UTF-8''W-9%20%28signed%29%2C%20Smith%20&%20Sons.pdf
//
// `filename=` is an ASCII transliteration with everything outside
// [ A-Za-z0-9!#$+.^_`|~-] percent-escaped; `filename*=` is the real name,
// UTF-8 and percent-encoded (RFC 8187). Taking `filename=` as sent is what
// saved that W-9 as "W-9 %28signed%29%2C Smith %26 Sons.pdf".

/** A quoted-string's text with its backslash escapes undone, or a bare token as sent. */
function unquote(value: string): string {
  const trimmed = value.trim();
  if (!trimmed.startsWith('"')) return trimmed;
  return trimmed.replace(/^"|"$/g, '').replace(/\\(.)/g, '$1');
}

/** Each `; name=value`, keyed by lowercased name. A repeated name keeps its first value. */
function parameters(header: string): Map<string, string> {
  const params = new Map<string, string>();
  // A quoted value can hold a `;` of its own, so it is matched whole.
  const parameter = /(?:^|;)\s*([^\s;=]+)\s*=\s*("(?:[^"\\]|\\.)*"|[^;]*)/g;
  for (let match = parameter.exec(header); match !== null; match = parameter.exec(header)) {
    const name = match[1].toLowerCase();
    if (!params.has(name)) params.set(name, unquote(match[2]));
  }
  return params;
}

/** `filename*`'s charset'language'value, decoded. Null unless it is UTF-8 and decodes. */
function decodeExtValue(value: string | undefined): string | null {
  const match = /^([^']*)'[^']*'(.*)$/.exec(value ?? '');
  if (match === null || match[1].toLowerCase() !== 'utf-8') return null;
  try {
    return decodeURIComponent(match[2]);
  } catch {
    return null;
  }
}

/** `filename`, percent-decoded, or as sent when it is not valid percent-encoding. */
function decodePlainValue(value: string | undefined): string | null {
  if (value === undefined) return null;
  try {
    return decodeURIComponent(value);
  } catch {
    return value; // "100% done.pdf" is a name, not an escape
  }
}

/** The last path segment, so a name never carries a directory. Null when nothing usable is left. */
function baseName(name: string | null): string | null {
  if (name === null) return null;
  const segments = name.split(/[/\\]/);
  const base = segments[segments.length - 1].trim();
  return base === '' || base === '.' || base === '..' ? null : base;
}

/**
 * The filename a Content-Disposition header value names, or null when it
 * names none, so the caller keeps its own fallback. `filename*=` wins when it
 * is UTF-8 and decodes (RFC 6266 §4.3); `filename=` is the fallback.
 */
export function filenameFromContentDisposition(header: string | null | undefined): string | null {
  if (!header) return null;
  const params = parameters(header);
  return (
    baseName(decodeExtValue(params.get('filename*'))) ??
    baseName(decodePlainValue(params.get('filename')))
  );
}

/** `filenameFromContentDisposition` for a download response. */
export function dispositionFilename(response: Response): string | null {
  return filenameFromContentDisposition(response.headers?.get?.('content-disposition'));
}
